import { useEffect, useState, type ReactNode } from "react";
import { Banner, Button, Checkbox, Field, Input, KeyValues, useToast } from "@/shared/rd";
import { Eyebrow, Meta } from "../rd-local";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import type { SubscriptionPlanType } from "@/lib/api/types";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateStoreSubscriptionPaymentMutation } from "@/shared/stores/services/mutations/useCreateStoreSubscriptionPaymentMutation";
import { useCreateSubscriptionPaymentProofUploadMutation } from "@/shared/stores/services/mutations/useCreateSubscriptionPaymentProofUploadMutation";
import { useStoreSubscriptionCurrentScheduleQuery } from "@/shared/stores/services/queries/useStoreSubscriptionCurrentScheduleQuery";

type Props = {
  storeId: string;
  /**
   * On a PER_ORDER_CREDIT store this same endpoint is a wallet top-up: there is
   * no schedule to settle, the money just lands in the wallet and settles the
   * oldest open charges from there.
   */
  planType?: SubscriptionPlanType;
  compact?: boolean;
  defaultOpen?: boolean;
  hideToggle?: boolean;
  onSuccess?: () => void;
  /** Lets a drawer put the submit in its own bar (`form={formId}`). */
  formId?: string;
  /** Hides the in-form submit when a drawer shows it. */
  hideSubmit?: boolean;
  /** Reports the submitting state to a drawer's own submit. */
  onBusyChange?: (busy: boolean) => void;
};

export function SubscriptionPaymentSettleForm({
  storeId,
  planType,
  compact,
  defaultOpen,
  hideToggle,
  onSuccess,
  formId: formIdProp,
  hideSubmit,
  onBusyChange,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(
    typeof defaultOpen === "boolean" ? defaultOpen : !compact,
  );
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    paidAt?: string;
    amount?: string;
  }>({});
  const { mutateAsync: createPayment, isPending: isCreatingPayment } =
    useCreateStoreSubscriptionPaymentMutation();
  const { mutateAsync: createProofUpload, isPending: isCreatingProofUpload } =
    useCreateSubscriptionPaymentProofUploadMutation();
  const {
    data: scheduleData,
    isLoading: isLoadingSchedule,
    refetch: refetchSchedule,
  } = useStoreSubscriptionCurrentScheduleQuery({
    storeId: isOpen ? storeId : "",
  });
  const schedule = scheduleData ?? null;
  const isPerOrderCredit =
    planType === SUBSCRIPTION_PLAN_TYPE.PER_ORDER_CREDIT ||
    planType === "FREE" ||
    planType === "PREMIUM";
  const canMarkAsPaid =
    !isPerOrderCredit && (schedule?.remainingAmount ?? 0) > 0;
  const effectiveMarkAsPaid = canMarkAsPaid && markAsPaid;
  const walletBalance = schedule?.creditBalance ?? 0;

  const formId = formIdProp ?? `settle-payment-${storeId}`;
  const isBusy = isSubmitting || isCreatingPayment || isCreatingProofUpload;
  const submitLabel = isPerOrderCredit ? t(keys.admin.subscriptionWalletTopUpAction) : t(keys.common.save);
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  const getMutationErrorMessage = (caughtError: unknown) => {
    if (caughtError instanceof ApiJsonClientError) {
      return caughtError.message || t(keys.errors.generic);
    }
    return t(keys.errors.generic);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void refetchSchedule();
  }, [isOpen, refetchSchedule, storeId]);

  const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
      date,
    );
  };

  const formatAmount = (
    amount: number | null | undefined,
    currency?: string,
    // Wallet money is spent in per-order slices, so it keeps its decimals.
    withDecimals = false,
  ) => {
    if (amount === null || amount === undefined) {
      return "-";
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        maximumFractionDigits: withDecimals ? 2 : 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency ?? ""}`.trim();
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setError(null);
    const nextFieldErrors: { paidAt?: string; amount?: string } = {};
    const formData = new FormData(form);
    const paidAt = formData.get("paidAt");
    const amount = formData.get("amount");
    const paidAtString = typeof paidAt === "string" ? paidAt.trim() : "";
    const amountString = typeof amount === "string" ? amount.trim() : "";

    if (paidAtString) {
      const parsedPaidAt = new Date(`${paidAtString}T00:00:00`);
      if (Number.isNaN(parsedPaidAt.getTime())) {
        nextFieldErrors.paidAt = t(keys.admin.subscriptionPaymentPaidAtInvalid);
      } else {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (parsedPaidAt > today) {
          nextFieldErrors.paidAt = t(
            keys.admin.subscriptionPaymentPaidAtFuture,
          );
        }
      }
    }

    if (!effectiveMarkAsPaid) {
      if (!amountString) {
        nextFieldErrors.amount = isPerOrderCredit
          ? t(keys.admin.subscriptionWalletTopUpAmountRequired)
          : t(keys.admin.subscriptionPaymentAmountRequired);
      } else {
        const parsedAmount = Number(amountString);
        if (!Number.isFinite(parsedAmount)) {
          nextFieldErrors.amount = t(
            keys.admin.subscriptionPaymentAmountInvalid,
          );
        } else if (parsedAmount <= 0) {
          nextFieldErrors.amount = t(
            keys.admin.subscriptionPaymentAmountPositive,
          );
        }
      }
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    const proofFile = formData.get("proofFile");

    let proofObjectKey: string | undefined;
    let proofContentType: string | undefined;
    let proofSizeBytes: number | undefined;

    if (proofFile instanceof File && proofFile.size > 0) {
      let uploadPayload: { uploadUrl: string; objectKey: string };
      try {
        uploadPayload = await createProofUpload({
          storeId,
          fileName: proofFile.name,
          contentType: proofFile.type || "application/octet-stream",
          sizeBytes: proofFile.size,
        });
      } catch (caughtError) {
        setError(getMutationErrorMessage(caughtError));
        setIsSubmitting(false);
        return;
      }

      const putResponse = await fetch(uploadPayload.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": proofFile.type || "application/octet-stream",
        },
        body: proofFile,
      });

      if (!putResponse.ok) {
        setError(t(keys.products.uploadFailed));
        setIsSubmitting(false);
        return;
      }

      proofObjectKey = uploadPayload.objectKey;
      proofContentType = proofFile.type || "application/octet-stream";
      proofSizeBytes = proofFile.size;
    }

    try {
      await createPayment({
        storeId,
        paidAt: paidAtString
          ? new Date(`${paidAtString}T00:00:00`).toISOString()
          : undefined,
        amount: effectiveMarkAsPaid
          ? schedule?.remainingAmount
          : amountString
            ? Number(amountString)
            : undefined,
        markAsPaid: effectiveMarkAsPaid,
        proofObjectKey,
        proofContentType,
        proofSizeBytes,
      });
    } catch (caughtError) {
      setError(getMutationErrorMessage(caughtError));
      setIsSubmitting(false);
      return;
    }

    const recorded = formatAmount(
      effectiveMarkAsPaid ? schedule?.remainingAmount : Number(amountString),
      schedule?.currency,
      true,
    );
    form.reset();
    setMarkAsPaid(false);
    setFieldErrors({});
    toast.show({
      message: t(isPerOrderCredit ? "admin.walletToppedUpToast" : "admin.paymentRecordedToast", { amount: recorded }),
    });
    setIsSubmitting(false);
    void refetchSchedule();
    onSuccess?.();
  };

  const scheduleRows: Array<{ label: ReactNode; value: ReactNode }> = schedule
    ? isPerOrderCredit
      ? [
          {
            label: t(keys.admin.storeSubscriptionsWalletBalance),
            value: (
              <span className="rd-num" style={{ fontWeight: 600 }}>
                {formatAmount(walletBalance, schedule.currency, true)}
              </span>
            ),
          },
        ]
      : [
          { label: t(keys.admin.subscriptionScheduleDueDate), value: formatDate(schedule.dueDate) },
          { label: t(keys.admin.subscriptionScheduleStatus), value: schedule.status },
          {
            label: t(keys.admin.subscriptionSchedulePaid),
            value: <span className="rd-num">{formatAmount(schedule.paidAmount, schedule.currency)}</span>,
          },
          {
            label: t(keys.admin.subscriptionScheduleRemaining),
            value: <span className="rd-num">{formatAmount(schedule.remainingAmount, schedule.currency)}</span>,
          },
          {
            label: t(keys.admin.storeSubscriptionsWalletBalance),
            value: <span className="rd-num">{formatAmount(walletBalance, schedule.currency, true)}</span>,
          },
        ]
    : [];
  const clearField = (field: "paidAt" | "amount") =>
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {compact && !hideToggle ? (
        <div>
          <Button size="sm" onClick={() => setIsOpen((prev) => !prev)}>
            {isOpen
              ? t(keys.common.hide)
              : isPerOrderCredit
                ? t(keys.admin.subscriptionWalletTopUpAction)
                : t(keys.admin.storeSubscriptionsSettlePayment)}
          </Button>
        </div>
      ) : null}
      {isOpen ? (
        <form id={formId} style={{ display: "flex", flexDirection: "column", gap: 14 }} onSubmit={handleSubmit}>
          {error ? (
            <Banner tone="error" title={t(isPerOrderCredit ? "admin.walletTopUpFailedTitle" : "admin.paymentRecordFailedTitle")}>
              {error} {t("admin.nothingChanged")}
            </Banner>
          ) : null}
          <div
            style={{
              padding: "12px 14px",
              borderRadius: "var(--rd-radius-lg)",
              background: "var(--rd-surface-soft)",
              boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <Eyebrow>
              {isPerOrderCredit ? t(keys.admin.subscriptionWalletTitle) : t(keys.admin.subscriptionScheduleCurrentTitle)}
            </Eyebrow>
            {isLoadingSchedule ? (
              <Meta>{t(keys.common.loading)}</Meta>
            ) : schedule ? (
              <>
                <KeyValues rows={scheduleRows} />
                {/* "Customer data stays hidden until topped up" is the
                    per-order-credit rule. A DEFAULT (Free/Premium) store is not
                    charged per new order, so it gets no such hint. */}
                {isPerOrderCredit &&
                walletBalance <= 0 &&
                planType === SUBSCRIPTION_PLAN_TYPE.PER_ORDER_CREDIT ? (
                  <Meta>{t(keys.admin.subscriptionWalletEmptyHint)}</Meta>
                ) : null}
              </>
            ) : (
              <Meta>{t(keys.common.emptyTitle)}</Meta>
            )}
          </div>
          <Field label={t(keys.admin.subscriptionPaymentPaidAtLabel)} error={fieldErrors.paidAt} htmlFor={`${formId}-paidAt`}>
            <Input
              id={`${formId}-paidAt`}
              name="paidAt"
              type="date"
              invalid={Boolean(fieldErrors.paidAt)}
              onChange={() => clearField("paidAt")}
              style={{ maxWidth: 200 }}
            />
          </Field>
          <Field
            label={
              isPerOrderCredit
                ? t(keys.admin.subscriptionWalletTopUpAmountLabel)
                : t(keys.admin.subscriptionPaymentAmountLabel)
            }
            error={fieldErrors.amount}
            htmlFor={`${formId}-amount`}
          >
            <Input
              id={`${formId}-amount`}
              name="amount"
              type="number"
              inputMode="decimal"
              min={0}
              step={0.01}
              prefix={schedule?.currency ?? "EGP"}
              disabled={effectiveMarkAsPaid}
              invalid={Boolean(fieldErrors.amount)}
              onChange={() => clearField("amount")}
              style={{ maxWidth: 220 }}
            />
          </Field>
          {isPerOrderCredit ? null : (
            <Checkbox
              label={t(keys.admin.subscriptionPaymentMarkAsPaid)}
              checked={effectiveMarkAsPaid}
              disabled={!canMarkAsPaid}
              onChange={(next) => {
                const checked = canMarkAsPaid && next;
                setMarkAsPaid(checked);
                if (checked) {
                  setFieldErrors((prev) => ({ ...prev, amount: undefined }));
                }
              }}
            />
          )}
          <Field
            label={t(keys.admin.subscriptionPaymentProofLabel)}
            help={t(keys.admin.subscriptionPaymentProofHelper)}
            htmlFor={`${formId}-proof`}
          >
            {/* eslint-disable-next-line no-restricted-syntax -- a file picker: the kit has no file control, and the form reads it through FormData. */}
            <input
              id={`${formId}-proof`}
              name="proofFile"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}
            />
          </Field>
          {hideSubmit ? null : (
            <div>
              <Button type="submit" variant="primary" loading={isBusy} loadingLabel={t("admin.saving")}>
                {submitLabel}
              </Button>
            </div>
          )}
        </form>
      ) : null}
    </div>
  );
}
